import React from "react";
import Chart from "../../components/chart";
import { num } from "../sales/territory-common";

/*
 * Inventory Planner: shared chart + date primitives.
 * Everything here is unit counts, never money (owner surface; money rule).
 * Data contracts (/v1/inventory/* + /v1/forecast/restock):
 *   trend point   = { period, qtyAvailable, inTransit, backordered, atp, outOfStock }
 *   restock row   = { sku, title, category, basis(forecast|velocity), atp, backordered, inTransit,
 *                     unitsPerMonthForecast, stockoutDate, leadTimeDays, orderBy,
 *                     daysUntilOrderBy, status(past|due|ok), suggestedQty }
 * AtpHistoryChart is also used by the sales product detail page.
 */

export const day = d => d ? String(d).slice(0, 10) : null;
const t = d => new Date(day(d) + "T00:00:00").getTime();

// "Dec 2" this year, "Dec 2, 2027" otherwise.
export function niceDate(d) {
  const iso = day(d);
  if (!iso) return "";
  const dt = new Date(iso + "T00:00:00");
  if (isNaN(dt.getTime())) return iso;
  const sameYear = dt.getFullYear() === new Date().getFullYear();
  return dt.toLocaleDateString("en-US", sameYear ? {
    month: "short",
    day: "numeric"
  } : {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

// ── §3 Product detail: ATP history & inbound (step line, stockout bands) ──────
export function AtpHistoryChart({
  inventory,
  nextAvailable,
  inboundQty,
  height = 210
}) {
  const pts = (inventory || []).map(p => ({
    date: day(p.period),
    atp: Number(p.atp) || 0,
    bo: Number(p.backordered) || 0,
    it: Number(p.inTransit) || 0
  })).filter(p => p.date).sort((a, b) => t(a.date) - t(b.date));
  if (pts.length < 2) return null;
  const W = 960,
    H = height,
    padL = 40,
    padR = 16,
    padT = 26,
    padB = 26;
  const t0 = t(pts[0].date);
  let t1 = t(pts[pts.length - 1].date);
  const naT = nextAvailable ? t(nextAvailable) : null;
  if (naT && naT > t1) t1 = naT; // extend axis to reach the inbound marker
  const span = Math.max(1, t1 - t0);
  const x = d => padL + (t(day(d)) - t0) / span * (W - padL - padR);
  const vmax = Math.max(1, ...pts.map(p => p.atp), Number(inboundQty) || 0);
  const y = v => padT + (1 - Math.max(0, v) / vmax) * (H - padT - padB);
  const y0 = y(0);

  // Step-after path: hold each level until the next reading.
  let dpath = `M${x(pts[0].date).toFixed(1)},${y(pts[0].atp).toFixed(1)}`;
  for (let i = 1; i < pts.length; i++) {
    dpath += ` H${x(pts[i].date).toFixed(1)} V${y(pts[i].atp).toFixed(1)}`;
  }

  // Stockout spans = contiguous segments whose starting reading is atp<=0.
  const bands = [];
  for (let i = 0; i < pts.length - 1; i++) {
    if (pts[i].atp <= 0) {
      const from = x(pts[i].date),
        to = x(pts[i + 1].date);
      const days = Math.round((t(pts[i + 1].date) - t(pts[i].date)) / 86400000);
      if (bands.length && Math.abs(bands[bands.length - 1].to - from) < 0.5) {
        bands[bands.length - 1].to = to;
        bands[bands.length - 1].days += days;
      } else bands.push({
        from,
        to,
        days
      });
    }
  }
  const lastBo = pts[pts.length - 1].bo;
  const grid = [0, 0.5, 1].map(f => Math.round(vmax * f));
  return /*#__PURE__*/React.createElement("svg", {
    viewBox: `0 0 ${W} ${H}`,
    style: {
      width: "100%",
      display: "block"
    }
  }, grid.map((v, i) => /*#__PURE__*/React.createElement("g", {
    key: i
  }, /*#__PURE__*/React.createElement("line", {
    x1: padL,
    x2: W - padR,
    y1: y(v),
    y2: y(v),
    stroke: "var(--plk-border, #eceef1)",
    strokeWidth: "1"
  }), /*#__PURE__*/React.createElement("text", {
    x: padL - 6,
    y: y(v) + 3,
    textAnchor: "end",
    fontSize: "10",
    fill: "var(--plk-text-dim, #9aa4b2)"
  }, num(v)))), bands.map((b, i) => /*#__PURE__*/React.createElement("g", {
    key: `b${i}`
  }, /*#__PURE__*/React.createElement("rect", {
    x: b.from,
    y: padT,
    width: Math.max(1, b.to - b.from),
    height: y0 - padT,
    fill: "rgba(180,35,24,.09)"
  }), /*#__PURE__*/React.createElement("text", {
    x: (b.from + b.to) / 2,
    y: padT - 8,
    textAnchor: "middle",
    fontSize: "11",
    fill: "#b42318"
  }, "stockout \xB7 ", b.days, " d"))), /*#__PURE__*/React.createElement("path", {
    d: dpath,
    fill: "none",
    stroke: "var(--plk-text, #1f2430)",
    strokeWidth: "2",
    strokeLinejoin: "round"
  }), naT != null && (() => {
    const mx = x(nextAvailable);
    const rightHalf = mx > W * 0.6; // keep the label on-canvas near the right edge
    return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("line", {
      x1: mx,
      x2: mx,
      y1: padT,
      y2: y0,
      stroke: "var(--plk-text-dim, #667085)",
      strokeWidth: "1.5",
      strokeDasharray: "4 3"
    }), /*#__PURE__*/React.createElement("circle", {
      cx: mx,
      cy: padT,
      r: "3.5",
      fill: "#2f7d4f"
    }), /*#__PURE__*/React.createElement("text", {
      x: rightHalf ? mx - 7 : mx + 7,
      y: padT + 4,
      textAnchor: rightHalf ? "end" : "start",
      fontSize: "11",
      fill: "var(--plk-text, #1f2430)"
    }, "container lands", Number(inboundQty) > 0 ? ` (+${num(inboundQty)})` : ""));
  })(), lastBo > 0 && /*#__PURE__*/React.createElement("text", {
    x: W - padR,
    y: y0 + 16,
    textAnchor: "end",
    fontSize: "11",
    fill: "#b42318"
  }, "backorders ", num(lastBo)), /*#__PURE__*/React.createElement("text", {
    x: padL,
    y: H - 6,
    fontSize: "10",
    fill: "var(--plk-text-dim, #9aa4b2)"
  }, pts[0].date), /*#__PURE__*/React.createElement("text", {
    x: W - padR,
    y: H - 6,
    textAnchor: "end",
    fontSize: "10",
    fill: "var(--plk-text-dim, #9aa4b2)"
  }, day(nextAvailable) || pts[pts.length - 1].date));
}

// ── §2 Out-of-stock SKU count over time (the stockout-impact line) ────────────
export function OosTrendChart({
  inventory,
  height = 180
}) {
  const raw = inventory || [];
  // Only render once the backend actually carries per-period outOfStock (added
  // to the trend payload 2026-09-07); before that the caller shows the count.
  if (!raw.some(p => p.outOfStock !== undefined && p.outOfStock !== null)) return null;
  const pts = raw.map(p => ({
    date: day(p.period),
    oos: Number(p.outOfStock) || 0
  })).filter(p => p.date);
  if (pts.length < 2) return null;
  // Shared chart adapter (components/chart.jsx), same look as the other app charts.
  const data = [["Week", "Items out of stock"], ...pts.map(p => [niceDate(p.date), p.oos])];
  const options = {
    legend: {
      position: "none"
    },
    colors: ["#ff6358"],
    series: {
      0: {
        type: "line"
      }
    },
    vAxis: {
      format: "short",
      minValue: 0
    },
    hAxis: {
      slantedText: false
    },
    pointSize: 5,
    tooltip: {
      format: "n0"
    }
  };
  return /*#__PURE__*/React.createElement(Chart, {
    chartType: "LineChart",
    data: data,
    options: options,
    height: `${height}px`
  });
}

// ── §2 Category trend: units sold by month (columns) vs stock available (line) ──
// Rendered with the shared chart adapter (components/chart.jsx). The stock line
// only exists from the first inventory snapshot; earlier months are gaps.
const monthKey = p => String(day(p) || "").slice(0, 7);
function monthLabel(key) {
  const d = new Date(key + "-01T00:00:00");
  if (isNaN(d.getTime())) return key;
  return d.toLocaleDateString("en-US", d.getMonth() === 0 ? {
    month: "short",
    year: "numeric"
  } : {
    month: "short"
  });
}

// Months from `start` (YYYY-MM-DD) through `end`, so empty months still get a slot.
function monthsBetween(start, end) {
  const out = [];
  const s = new Date(day(start) + "T00:00:00"),
    e = new Date(day(end) + "T00:00:00");
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return out;
  const d = new Date(s.getFullYear(), s.getMonth(), 1);
  while (d <= e && out.length < 60) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    d.setMonth(d.getMonth() + 1);
  }
  return out;
}
export function trendRows(trend) {
  if (!trend) return [];
  const sold = {};
  (trend.sales || []).forEach(p => {
    const k = monthKey(p.period);
    if (k) sold[k] = (sold[k] || 0) + (Number(p.units) || 0);
  });
  const stock = {};
  (trend.inventory || []).forEach(p => {
    const k = monthKey(p.period);
    if (k) stock[k] = Math.max(0, Number(p.atp) || 0);
  });
  let keys = monthsBetween(trend.start, trend.end);
  if (!keys.length) keys = Array.from(new Set([...Object.keys(sold), ...Object.keys(stock)])).sort();
  return keys.map(k => ({
    key: k,
    label: monthLabel(k),
    sold: sold[k] || 0,
    stock: k in stock ? stock[k] : null
  }));
}
export function CategoryTrendChart({
  trend,
  height = 260
}) {
  const rows = trendRows(trend);
  if (!rows.length) return null;
  // A line needs two readings. With a single stock snapshot (stock tracking
  // just began, or the stock feed has not run since) one lone marker on its own
  // zoomed axis read as a broken chart; the caller states that reading in text.
  const hasStock = rows.filter(r => r.stock != null).length >= 2;
  const header = hasStock ? ["Month", "Units sold", "Available to sell"] : ["Month", "Units sold"];
  const data = [header, ...rows.map(r => hasStock ? [r.label, r.sold, r.stock] : [r.label, r.sold])];
  const options = {
    legend: {
      position: "bottom"
    },
    colors: ["#ff6358", "#232020"],
    series: hasStock ? {
      0: {
        type: "column"
      },
      1: {
        type: "line",
        targetAxisIndex: 1
      }
    } : {
      0: {
        type: "column"
      }
    },
    vAxes: hasStock ? [{
      title: "Units sold",
      format: "short",
      minValue: 0
    }, {
      title: "Available",
      format: "short",
      minValue: 0
    }] : [{
      title: "Units sold",
      format: "short",
      minValue: 0
    }],
    pointSize: 5,
    hAxis: {
      slantedText: false
    },
    tooltip: {
      format: "n0"
    }
  };
  return /*#__PURE__*/React.createElement(Chart, {
    chartType: "ComboChart",
    data: data,
    options: options,
    height: `${height}px`
  });
}